// Playoff bracket geometry shared by the on-screen division scheduler
// (DivisionPage) and the downloadable PDF scoresheet (utils/scoresheets/builders).
//
// A division's playoff bracket size is stored on `division.config.playoffTeams`
// (2 | 4 | 8). Since the auto-advancement feature, the geometry itself —
// slot keys, labels, seed pairings, and winner/loser routing — lives in
// `@staty/shared/utils/bracketTopology` (also consumed by the API's advancement
// engine); this module adapts it for the admin's existing call sites.

import {
  getBracketSlots,
  bracketSeedPairs,
  normalizeBracketSize,
  isConsolationFormat,
  BracketSlotDef,
} from '@staty/shared/utils/bracketTopology';

export type PlayoffStage = string;

export type PlayoffSlot = BracketSlotDef;

// Normalize an arbitrary stored value to one of the supported sizes (2/4/8).
export function normalizePlayoffSize(size?: number): 2 | 4 | 8 {
  return normalizeBracketSize(size, false);
}

// Ordered playoff slots (earliest round first) for a given bracket size.
// Single-elimination geometry — used by the round-robin / pool-play playoff
// columns and the standalone 'playoffs' / 'bracket' formats.
export function getPlayoffSlots(size?: number): PlayoffSlot[] {
  return getBracketSlots(size, false);
}

// Ordered slots for a standalone bracket format, consolation-aware.
export function getPlayoffSlotsForFormat(format: string | undefined, size?: number): PlayoffSlot[] {
  return getBracketSlots(size, isConsolationFormat(format));
}

// Standard single-elimination first-round seed pairings so the top seeds can
// only meet in the final. Each inner pair is [highSeed, lowSeed].
export function bracketSeeds(size?: number): number[][] {
  return bracketSeedPairs(size, false);
}

// Default bracket size when config.playoffTeams is unset. Standalone bracket /
// playoffs formats historically implied 8; round-robin/pool playoffs intend 4.
export function defaultPlayoffTeams(format?: string): number {
  return format === 'bracket' || format === 'playoffs' || format === 'playoffs-consolation'
    ? 8
    : 4;
}
